import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowRight,
    Hand,
    Activity,
    Brain,
    CalendarDays,
    MapPin,
    Clock,
    Trophy,
    BookOpen,
    Download,
    User as UserIcon,
    Compass,
    CheckCircle2,
    AlertCircle,
} from "lucide-react";
import { api, formatApiError, useAuth } from "@/lib/api";
import { toast } from "sonner";
import { useStagiaireActions } from "@/components/stagiaire/StagiaireActionsContext";

const ICONS = { Hand, Activity, Brain };

function formatDate(iso) {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
}

function daysUntil(iso) {
    if (!iso) return 0;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const target = new Date(iso);
    target.setHours(0, 0, 0, 0);
    return Math.round((target - now) / (1000 * 60 * 60 * 24));
}

function CursusProgressCard({ progress }) {
    const Icon = ICONS[progress.icon] || Hand;
    const segments = progress.formations || [];
    return (
        <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-2xl border border-brand-line bg-white p-7 shadow-premium"
            data-testid={`cursus-progress-${progress.slug}`}
        >
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange ring-1 ring-brand-orange/20">
                        <Icon className="h-5 w-5" />
                    </div>
                    <div>
                        <h3 className="font-display text-2xl text-brand-ink leading-tight">{progress.name.replace("Cursus ", "")}</h3>
                        <p className="mt-1 text-xs italic text-brand-orange">{progress.tagline}</p>
                    </div>
                </div>
                {progress.is_complete && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-brand-orange px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">
                        <Trophy className="h-3 w-3" /> Cursus complet
                    </span>
                )}
            </div>

            {/* Segmented progress bar */}
            <div className="mt-6 grid grid-cols-3 gap-1.5">
                {segments.map((seg) => {
                    let cls = "h-2 rounded-full ";
                    if (seg.state === "completed") cls += "bg-brand-orange";
                    else if (seg.state === "upcoming") cls += "bg-brand-orange/40 animate-pulse";
                    else cls += "bg-brand-line";
                    return <div key={seg.slug} className={cls} data-testid={`segment-${progress.slug}-${seg.slug}`} title={`${seg.name} — ${seg.state}`} />;
                })}
            </div>

            <div className="mt-4 text-sm text-brand-ink/75">
                <strong className="font-display text-2xl text-brand-orange">{progress.completed}</strong>
                <span className="text-brand-smoke">/</span>
                <strong className="text-brand-ink">{progress.total}</strong>{" "}
                formation{progress.completed > 1 ? "s" : ""} complétée{progress.completed > 1 ? "s" : ""}{" "}
                · <span className="text-brand-orange">{progress.upcoming}</span> à venir{" "}
                · <span className="text-brand-smoke">{progress.remaining}</span> restante{progress.remaining > 1 ? "s" : ""}
            </div>

            {/* Statut Expert PA progress */}
            {progress.certificate_title && (
                <div
                    className={`mt-5 rounded-2xl border px-4 py-3 ${
                        progress.completed >= 3
                            ? "border-brand-orange bg-brand-orange/10"
                            : "border-dashed border-brand-ink/15 bg-brand-sand"
                    }`}
                    data-testid={`diploma-progress-${progress.slug}`}
                >
                    <div className="flex items-start gap-3">
                        <Trophy className={`mt-0.5 h-4 w-4 flex-shrink-0 ${progress.completed >= 3 ? "text-brand-orange" : "text-brand-smoke"}`} />
                        <div className="flex-1">
                            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand-orange">
                                Statut Expert PA
                            </div>
                            <div className="mt-1 text-sm font-semibold text-brand-ink">
                                {progress.certificate_title}
                            </div>
                            <div className="mt-1 text-xs text-brand-ink/70">
                                {progress.completed >= 3
                                    ? "🏆 Félicitations — vous franchissez ce palier vers le Statut d'Expert en Prévention Active."
                                    : `${progress.completed}/3 formations complétées. Finalisez ce cursus pour avancer vers le Statut Expert PA.`}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Link
                to={`/stagiaire/cursus/${progress.slug}`}
                data-testid={`cursus-progress-cta-${progress.slug}`}
                className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand-ink hover:text-brand-orange"
            >
                Voir le cursus <ArrowRight className="h-4 w-4 transition-transform hover:translate-x-1" />
            </Link>
        </motion.div>
    );
}

function NextSessionHero({ item }) {
    const session = item.session;
    const formation = session.formation || {};
    const cursus = session.cursus || {};
    const formateur = session.formateur;
    const days = daysUntil(session.start_date);
    return (
        <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden rounded-3xl shadow-premium-lg"
            data-testid="next-session-hero"
        >
            <img
                src={cursus.image_url}
                alt=""
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-brand-ink via-brand-ink/85 to-brand-ink/40" />
            <div className="relative flex flex-col gap-8 p-8 text-white md:p-12 lg:flex-row lg:items-end lg:justify-between">
                <div className="max-w-2xl">
                    <div className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-xs uppercase tracking-[0.22em] backdrop-blur">
                        <CalendarDays className="h-3 w-3 text-brand-orange" /> Prochaine session
                    </div>
                    <h2 className="mt-6 font-display text-3xl leading-tight md:text-5xl">
                        {formation.name}
                    </h2>
                    <p className="mt-2 text-sm italic text-brand-orange">{cursus.name}</p>
                    <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-white/85">
                        <span className="inline-flex items-center gap-2">
                            <CalendarDays className="h-4 w-4 text-brand-orange" /> {formatDate(session.start_date)}
                        </span>
                        <span className="inline-flex items-center gap-2">
                            <Clock className="h-4 w-4 text-brand-orange" /> {session.start_time}–{session.end_time}
                        </span>
                        <span className="inline-flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-brand-orange" />
                            {session.venue_room_name && <strong>{session.venue_room_name}, </strong>}
                            {session.venue_address}, {session.venue_postcode} {session.city}
                        </span>
                    </div>
                    {formateur && (
                        <div className="mt-6 inline-flex items-center gap-3 rounded-2xl border border-white/15 bg-white/[0.06] px-4 py-3 backdrop-blur">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-orange text-white font-display">
                                {(formateur.first_name || "?")[0]}
                            </div>
                            <div>
                                <div className="text-sm font-semibold">{formateur.name}</div>
                                <div className="text-xs text-white/60">Formateur</div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="flex flex-col gap-4">
                    <div className="rounded-2xl border border-white/15 bg-white/[0.06] p-5 text-center backdrop-blur">
                        <div className="font-display text-5xl text-brand-orange leading-none">{days}</div>
                        <div className="mt-2 text-[11px] uppercase tracking-wider text-white/70">jours restants</div>
                    </div>
                    <Link
                        to={`/stagiaire/sessions/${session.id}`}
                        data-testid="next-session-detail-cta"
                        className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-[#d9772c]"
                    >
                        Voir les détails <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
            </div>
        </motion.div>
    );
}

export default function StagiaireDashboard() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const { data: actions } = useStagiaireActions();

    useEffect(() => {
        api.get("/stagiaire/dashboard")
            .then((r) => setData(r.data))
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="mx-auto max-w-7xl px-6 py-10" data-testid="page-stagiaire-dashboard">
            <div>
                <div className="text-xs uppercase tracking-[0.25em] text-brand-orange">Espace stagiaire</div>
                <h1 className="mt-3 font-display text-5xl font-normal tracking-tightest leading-[0.95] sm:text-6xl">
                    Bonjour {user?.first_name}.
                </h1>
                <p className="mt-3 max-w-xl text-lg text-brand-smoke">
                    Votre parcours <em className="italic text-brand-orange">Prévention Active</em> est en marche.
                </p>
            </div>

            {/* ============ Guide rapide ============ */}
            <section
                className="mt-8 rounded-2xl border border-brand-line bg-white p-6"
                data-testid="dashboard-guide"
            >
                <div className="flex items-start gap-3">
                    <Compass className="mt-1 h-5 w-5 flex-shrink-0 text-brand-orange" />
                    <div>
                        <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-orange">
                            Bienvenue dans votre espace
                        </div>
                        <p className="mt-2 text-sm text-brand-ink/80">
                            Cette page est votre <strong>tableau de bord</strong>. Vous y retrouvez vos actions à faire,
                            votre progression par cursus et votre prochaine session.
                            Les rubriques à gauche vous permettent de gérer vos cursus, sessions, documents et profil.
                            <strong className="ml-1 text-brand-ink">
                                Un chiffre orange à côté d&apos;une rubrique = une action à faire.
                            </strong>
                        </p>
                    </div>
                </div>
            </section>

            {/* ============ Actions requises ============ */}
            {(actions?.items?.length || 0) > 0 ? (
                <section
                    className="mt-8 rounded-3xl border border-brand-orange/30 bg-brand-orange/[0.06] p-6 sm:p-8"
                    data-testid="dashboard-actions-required"
                >
                    <div className="flex items-center gap-3">
                        <AlertCircle className="h-5 w-5 text-brand-orange" />
                        <h2 className="font-display text-2xl text-brand-ink">
                            Actions à faire
                            <span className="ml-2 rounded-full bg-brand-orange px-2.5 py-0.5 text-xs font-semibold text-white align-middle">
                                {actions.total}
                            </span>
                        </h2>
                    </div>
                    <p className="mt-2 text-sm text-brand-ink/70">
                        Ces étapes vous permettent de valider votre parcours et de télécharger vos documents officiels.
                    </p>
                    <div className="mt-5 grid gap-3">
                        {actions.items.map((item, i) => (
                            <Link
                                key={`${item.type}-${i}`}
                                to={item.cta_url}
                                data-testid={`dashboard-action-${item.type}-${i}`}
                                className="group flex items-start gap-4 rounded-2xl border border-brand-line bg-white p-4 transition-all hover:-translate-y-0.5 hover:border-brand-orange/50 hover:shadow-premium"
                            >
                                <div
                                    className={`mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${
                                        item.urgency === "high"
                                            ? "bg-red-100 text-red-700"
                                            : item.urgency === "medium"
                                              ? "bg-brand-orange/15 text-brand-orange"
                                              : "bg-green-100 text-green-700"
                                    }`}
                                >
                                    {item.urgency === "low" ? (
                                        <CheckCircle2 className="h-4 w-4" />
                                    ) : (
                                        <AlertCircle className="h-4 w-4" />
                                    )}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-semibold text-brand-ink">{item.label}</div>
                                    <div className="mt-1 text-xs text-brand-smoke">{item.detail}</div>
                                </div>
                                <div className="mt-1 inline-flex flex-shrink-0 items-center gap-1 text-xs font-semibold text-brand-orange group-hover:gap-2 transition-all">
                                    {item.cta_label}
                                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            ) : (
                actions !== null && (
                    <section
                        className="mt-8 flex items-center gap-3 rounded-2xl border border-green-200 bg-green-50 p-5"
                        data-testid="dashboard-actions-empty"
                    >
                        <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-green-700" />
                        <div className="text-sm text-green-900">
                            <strong>Tout est à jour.</strong> Aucune action requise pour le moment.
                        </div>
                    </section>
                )
            )}

            {loading ? (
                <div className="mt-10 text-brand-smoke">Chargement…</div>
            ) : (
                <>
                    {/* Stats */}
                    <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-3">
                        {[
                            { label: "Formations suivies", value: data?.stats?.attended_count ?? 0, icon: Trophy },
                            { label: "Heures de formation", value: data?.stats?.total_hours ?? 0, icon: Clock },
                            { label: "Sessions à venir", value: data?.stats?.upcoming_count ?? 0, icon: CalendarDays },
                        ].map((s) => {
                            const Icon = s.icon;
                            return (
                                <div key={s.label} className="rounded-2xl border border-brand-line bg-white p-5 shadow-premium">
                                    <Icon className="h-5 w-5 text-brand-orange" />
                                    <div className="mt-3 font-display text-3xl text-brand-ink">{s.value}</div>
                                    <div className="mt-1 text-xs uppercase tracking-wider text-brand-smoke">{s.label}</div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Cursus progress */}
                    {(data?.cursus_progress || []).length > 0 && (
                        <section className="mt-12">
                            <h2 className="font-display text-3xl">Ma progression par cursus</h2>
                            <p className="mt-1 text-sm text-brand-smoke">Suivez l'avancement de chaque cursus de Prévention Active.</p>
                            <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2">
                                {(data?.cursus_progress || []).map((p) => (
                                    <CursusProgressCard key={p.slug} progress={p} />
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Next session */}
                    {data?.next_session && (
                        <section className="mt-14">
                            <NextSessionHero item={data.next_session} />
                        </section>
                    )}

                    {/* Other upcoming */}
                    {(data?.upcoming_sessions || []).length > 0 && (
                        <section className="mt-12">
                            <h2 className="font-display text-2xl">Mes autres sessions à venir</h2>
                            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                                {(data?.upcoming_sessions || []).map((item) => (
                                    <Link
                                        key={item.id}
                                        to={`/stagiaire/sessions/${item.session.id}`}
                                        className="rounded-2xl border border-brand-line bg-white p-6 shadow-premium hover:-translate-y-0.5 hover:shadow-premium-lg transition-all"
                                        data-testid={`upcoming-session-${item.session.id}`}
                                    >
                                        <div className="text-xs uppercase tracking-wider text-brand-orange">{formatDate(item.session.start_date)}</div>
                                        <div className="mt-3 font-display text-xl text-brand-ink">{item.session.formation_name}</div>
                                        <div className="mt-2 text-xs text-brand-smoke">{item.session.city} · {item.session.venue_room_name}</div>
                                    </Link>
                                ))}
                            </div>
                        </section>
                    )}

                    {(data?.cursus_progress || []).length === 0 && !data?.next_session && (
                        <section className="mt-12 rounded-3xl border border-dashed border-brand-line bg-white p-12 text-center">
                            <BookOpen className="mx-auto h-8 w-8 text-brand-orange" />
                            <h2 className="mt-6 font-display text-3xl">Démarrez votre parcours.</h2>
                            <p className="mt-3 text-brand-smoke">
                                Vous n'êtes inscrit à aucune session pour l'instant. Explorez nos cursus pour trouver votre prochaine formation.
                            </p>
                            <Link
                                to="/expertise-pa"
                                className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-3 text-sm font-semibold text-white"
                            >
                                Explorer les cursus <ArrowRight className="h-4 w-4" />
                            </Link>
                        </section>
                    )}
                </>
            )}
        </div>
    );
}
